/**
 * Route-owned search contracts. One CLOSED schema per owning route.
 *
 *   raw URL query          per-field preprocess           route search
 *   ?tab=a&tab=b   ──►  string|number|boolean → String  ──►  { }
 *   ?tab=2         ──►  anything else         → undefined ──► { tab: '2' }
 *
 * NO route declares an index signature: a key that is not named here is not
 * typed, so a duplicated query key can never reach a page typed `string`.
 * Unknown keys still survive in the URL — TanStack's non-strict search merge
 * keeps the raw values that these schemas strip from the validated output.
 */
import { getRouteApi, useLocation } from '@tanstack/react-router';
import { z } from 'zod';

function normalizeSearchString(value: unknown): string | undefined {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return undefined;
}

const optionalSearchString = z.preprocess(normalizeSearchString, z.string().optional());
// `.min(1)` alone admits '   ', which is truthy but identifies nothing — the page
// would render blank instead of the route rejecting the value. A required search
// field therefore means PRESENT AND NON-BLANK, matching the flag guard that feeds it.
const requiredSearchString = z.preprocess(
  normalizeSearchString,
  z.string().refine((value) => value.trim().length > 0)
);

const loginSchema = z.object({ redirectTo: optionalSearchString });
const aiInboxSchema = z.object({ threadId: optionalSearchString });
const accountRegisterSchema = z.object({
  accountReconciliationId: optionalSearchString,
  initialGridFilters: optionalSearchString,
  path: optionalSearchString,
  tab: optionalSearchString,
});
/** Five routes share exactly this contract; they must not drift apart. */
const tabSchema = z.object({ tab: optionalSearchString });
const settingsAccountingSchema = z.object({
  code: optionalSearchString,
  qboTab: optionalSearchString,
  realmId: optionalSearchString,
  state: optionalSearchString,
  tab: optionalSearchString,
});
const settingsEntitiesSchema = z.object({ ediProductId: optionalSearchString, tab: optionalSearchString });
const viewLotTrackingSchema = z.object({ adjustmentId: optionalSearchString, tab: optionalSearchString });
const documentEditorSchema = z.object({
  entity: optionalSearchString,
  lookup: optionalSearchString,
  name: optionalSearchString,
  new: optionalSearchString,
  type: optionalSearchString,
});
const palletTagEditorSchema = z.object({ lookup: optionalSearchString, name: optionalSearchString });
const biDashboardsSchema = z.object({ dashboardId: requiredSearchString });

export type LoginSearch = z.infer<typeof loginSchema>;
export type AccountRegisterSearch = z.infer<typeof accountRegisterSchema>;
export type TabSearch = z.infer<typeof tabSchema>;
export type SettingsAccountingSearch = z.infer<typeof settingsAccountingSchema>;
export type SettingsEntitiesSearch = z.infer<typeof settingsEntitiesSchema>;
export type ViewLotTrackingSearch = z.infer<typeof viewLotTrackingSchema>;
export type DocumentEditorSearch = z.infer<typeof documentEditorSchema>;
export type PalletTagEditorSearch = z.infer<typeof palletTagEditorSchema>;
export type BIDashboardsSearch = z.infer<typeof biDashboardsSchema>;

/**
 * Validators take the raw parsed query and return the closed schema. The
 * parameter deliberately does NOT carry TanStack's `SearchSchemaInput` marker:
 * without it the navigation input type is the RETURN type, which is what keeps
 * `dashboardId` required at every `linkOptions`/`navigate` call site.
 */
type RawSearch = Record<string, unknown>;

function routeSearchValidator<TSchema extends z.ZodTypeAny>(schema: TSchema) {
  return (search: RawSearch): z.infer<TSchema> => schema.parse(search);
}

export const loginSearchSchema = routeSearchValidator(loginSchema);

export const aiInboxSearchSchema = routeSearchValidator(aiInboxSchema);

export const accountRegisterSearchSchema = routeSearchValidator(accountRegisterSchema);

export const accountingReportsSearchSchema = routeSearchValidator(tabSchema);

export const agingReportsSearchSchema = routeSearchValidator(tabSchema);

export const settingsAccountingSearchSchema = routeSearchValidator(settingsAccountingSchema);

export const settingsBusinessSearchSchema = routeSearchValidator(tabSchema);

export const settingsEntitiesSearchSchema = routeSearchValidator(settingsEntitiesSchema);

export const settingsInventorySearchSchema = routeSearchValidator(tabSchema);

export const settingsTeamSearchSchema = routeSearchValidator(tabSchema);

export const viewLotTrackingSearchSchema = routeSearchValidator(viewLotTrackingSchema);

export const documentEditorSearchSchema = routeSearchValidator(documentEditorSchema);

export const palletTagEditorSearchSchema = routeSearchValidator(palletTagEditorSchema);

export const biDashboardsSearchSchema = routeSearchValidator(biDashboardsSchema);

const accountRegisterRouteApi = getRouteApi('/agriful-frame/accounting/accounts/register/{-$path}');
const settingsAccountingRouteApi = getRouteApi('/agriful-frame/settings/accounting');
const settingsBusinessRouteApi = getRouteApi('/agriful-frame/settings/business');
const settingsInventoryRouteApi = getRouteApi('/agriful-frame/settings/inventory');
const viewLotTrackingRouteApi = getRouteApi('/agriful-frame/inventory/lot-tracking/$warehouseId/$slug');
const documentEditorRouteApi = getRouteApi('/agriful-frame/internal/documents/editor');
const palletTagEditorRouteApi = getRouteApi('/agriful-frame/internal/pallet-tags/editor');

export function useAccountRegisterSearch(): AccountRegisterSearch {
  return accountRegisterRouteApi.useSearch();
}

export function useSettingsAccountingSearch(): SettingsAccountingSearch {
  return settingsAccountingRouteApi.useSearch();
}

export function useSettingsBusinessSearch(): TabSearch {
  return settingsBusinessRouteApi.useSearch();
}

export function useSettingsInventorySearch(): TabSearch {
  return settingsInventoryRouteApi.useSearch();
}

export function useViewLotTrackingSearch(): ViewLotTrackingSearch {
  return viewLotTrackingRouteApi.useSearch();
}

export function useDocumentEditorSearch(): DocumentEditorSearch {
  return documentEditorRouteApi.useSearch();
}

export function usePalletTagEditorSearch(): PalletTagEditorSearch {
  return palletTagEditorRouteApi.useSearch();
}

/**
 * Raw query text for widgets that mount under MANY routes and therefore own no
 * schema. Read it with `URLSearchParams`; never index it as if every key exists.
 */
export function useSearchString(): string {
  return useLocation({ select: (location) => location.searchStr });
}
